import {
  ChangeEvent,
  createContext,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { sanitizeValue } from '../lib/sanitize';
import { useOperator } from './OperatorContext';
import { useBranch } from '../hooks/Operator/useBranch';
import { ProgressStepSummary, useProgress } from '../hooks/useProgress';
import { getEventValueAsType } from '../lib/formatters';
import { CurrentProgress } from '../hooks/useProgress';
import { useAppView } from './AppViewContext';
import { useNavigate } from 'react-router-dom';
import { BASE_URL_MAP } from '../lib/environment';

interface CreateOperatorContext {
  branches: OperatorBranch[];
  creatingOperator: boolean;
  completed: boolean;
  defaultMediaFirstSettings: CreateOperatorMediaFirstSettings;
  defaultMediaRoomSettings: CreateOperatorMediaRoomSettings;
  loading: boolean;
  operator: CreateOperator;
  onBack(): void;
  onComplete(): void;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  onSubmit(event: FormEvent<HTMLFormElement>): void;
  progress: CurrentProgress;
  setOperator(operator: CreateOperator): void;
  summary: ProgressStepSummary[];
  updateOperator(field: keyof CreateOperator, value: any): void;
}

interface ApiResCreateOperator {
  ID: number;
  DefaultCreditLimit: number;
  Name: string;
  Code: string;
  TimeZoneName: string;
  MediaFirstFreeChannelGroup: string | null;
  ReachDeviceStreamLimit: number | null;
  MediaFirstTenantID: string | null;
  MediaFirstSubtenantID: string | null;
  CreditResetDay: number;
  AppKeyURL1: string | null;
  AppKeyURL2: string | null;
  AppKeyURL3: string | null;
  AppKeyURL4: string | null;
  EnhancedData: boolean;
  UseScreensaver: boolean;
  ShowSubscribedChannels: boolean;
  GratisAccounts: number;
  MediaRoom: boolean;
  MediaFirst: boolean;
  UsesTGOAuth?: boolean;
  BranchID: string | null;
  ChannelMapGroups: {
    MediaFirstID: string;
    Description: string;
    Active: boolean;
  }[];
  CloudDvrs?: {
    MediaFirstID: string;
    FeatureSubscriptionID: string;
    Description: string;
    Active: boolean;
  }[];
  DeviceModels: {
    Model: string;
    ClientVersion: string | null;
    CanDVR: boolean;
    System: string;
    Active: boolean;
    ClientStoreValue: string | null;
  }[];
  ExperienceGroups?: {
    MediaFirstID: string;
    Description: string;
    Active: boolean;
  }[];
  FIPSCodes: {
    Code: string;
    CountyName: string;
    Active: boolean;
  }[];
  RoutingGroups?: {
    MediaFirstID: string;
    Description: string;
    Active: boolean;
  }[];
  SubscriberGroups: {
    Name: string;
    Description: string;
    Base: boolean;
    AlaCarte: boolean;
    Active: boolean;
  }[];
  TransportTypes: {
    Name: string;
    IngressHD: number;
    IngressSD: number;
    WanHD: number;
    WanSD: number;
    Active: boolean;
  }[];
}

interface CreateOperatorProvider {
  children: ReactNode;
}

const CreateOperatorContext = createContext<CreateOperatorContext | undefined>(
  undefined
);

export const defaultMediaroomSettings: CreateOperatorMediaRoomSettings = {
  appKeyUrl1: null,
  appKeyUrl2: null,
  appKeyUrl3: null,
  appKeyUrl4: null,
  branch: null,
  rdpProfileName: null,
  showSubscribedChannelsOnly: false,
  useScreensaver: false,
};
export const defaultMediaFirstSettings: CreateOperatorMediaFirstSettings = {
  cloudDvrs: [],
  defaultDeviceStreamCount: null,
  experienceGroups: [],
  freeChannelGroup: null,
  recorderId: null,
  routingGroups: [],
  subTenantId: null,
  tenantId: null,
  usesTgAuth: false,
};

export const defaultAccountSettings = {
  creditResetDay: 5,
  defaultCreditLimit: 200,
  enhancedData: true,
  gratisAccounts: 0,
};

export const CreateOperatorProvider = ({
  children,
}: CreateOperatorProvider) => {
  const initialState = {
    id: 0,
    accountSettings: defaultAccountSettings,
    mediaFirstSettings: defaultMediaFirstSettings,
    mediaRoomSettings: defaultMediaroomSettings,
    deviceModels: [],
    fipsCodes: [],
    code: '',
    name: '',
    timeZone: '',
    subscriberGroups: [],
    transportTypes: [],
    systemTypeIDs: [],
  };

  const { loading: operatorLoading, operators } = useOperator();
  const { branches, loading: branchesLoading } = useBranch();
  const [creatingOperator, setCreatingOperator] = useState(false);
  const loading = operatorLoading || branchesLoading;
  const [form, setForm] = useState<CreateOperator>(initialState);
  const navigate = useNavigate();
  const appView = useAppView();

  const progress = useProgress(
    [
      {
        step: 1,
        name: 'Create Operator',
        axiosAction: (makeRequest) => {
          const sanitized = sanitizeValue(form);
          const body = mapResOperatorForApi(sanitized);

          makeRequest(`${BASE_URL_MAP['Residential']}/Operators`, 'post', body);
        },
        onComplete: (response) => {},
        onError: (reason) => {},
        continueOnError: false,
      },
    ],
    () => {}
  );

  useEffect(() => {
    if (appView.sector !== 'Residential' || appView.type !== 'NDP') {
      navigate('/');
    }
  }, [appView.sector, appView.type]);

  useEffect(() => {
    resetForm();
  }, []);

  const resetForm = () => setForm(initialState);

  const updateOperator = (key: keyof CreateOperator, value: any) => {
    setForm((values) => Object.assign({}, values, { [key]: value }));
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm(
      Object.assign({}, form, {
        [event.target.name]: getEventValueAsType(event),
      })
    );
  };

  const onBack = () => {
    setCreatingOperator(false);
    progress.reset();
  };

  const onComplete = () => {
    setCreatingOperator(false);
    resetForm();
    progress.reset();
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setCreatingOperator(true);
    progress.start();
  };

  const setOperator = (state: CreateOperator) => {
    setForm(Object.assign({}, state));
  };

  return (
    <CreateOperatorContext.Provider
      value={{
        branches,
        creatingOperator,
        completed: progress.completed,
        defaultMediaFirstSettings: defaultMediaFirstSettings,
        defaultMediaRoomSettings: defaultMediaroomSettings,
        operator: form,
        onBack,
        onChange,
        onComplete,
        progress,
        setOperator,
        loading,
        onSubmit,
        summary: progress.summary,
        updateOperator,
      }}
    >
      {children}
    </CreateOperatorContext.Provider>
  );
};

export const useCreateOperator = () => {
  const context = useContext(CreateOperatorContext);

  if (context === undefined)
    throw new Error(
      'useCreateOperator must be used within a CreateOperatorProvider'
    );

  return context;
};

const trimString = (value: string | null | undefined) => {
  if (value && value.trim() !== '') return value.trim();
  return null;
};

const mapResOperatorForApi = (
  operator: CreateOperator
): ApiResCreateOperator => ({
  ID: operator.id,
  DefaultCreditLimit: operator.accountSettings.defaultCreditLimit,
  Name: operator.name.trim(),
  Code: operator.code.trim(),
  TimeZoneName: operator.timeZone.trim(),
  MediaFirstFreeChannelGroup: trimString(
    operator.mediaFirstSettings?.freeChannelGroup
  ),
  ReachDeviceStreamLimit:
    operator.mediaFirstSettings?.defaultDeviceStreamCount ?? 0,
  MediaFirstTenantID: trimString(operator.mediaFirstSettings?.tenantId),
  MediaFirstSubtenantID: trimString(operator.mediaFirstSettings?.subTenantId),
  CreditResetDay: operator.accountSettings.creditResetDay ?? 5,
  AppKeyURL1: trimString(operator.mediaRoomSettings?.appKeyUrl1),
  AppKeyURL2: trimString(operator.mediaRoomSettings?.appKeyUrl2),
  AppKeyURL3: trimString(operator.mediaRoomSettings?.appKeyUrl3),
  AppKeyURL4: trimString(operator.mediaRoomSettings?.appKeyUrl4),
  UseScreensaver: operator.mediaRoomSettings?.useScreensaver ?? false,
  ShowSubscribedChannels:
    operator.mediaRoomSettings?.showSubscribedChannelsOnly ?? false,
  EnhancedData: operator.accountSettings.enhancedData ?? false,
  GratisAccounts: operator.accountSettings.gratisAccounts ?? 0,
  MediaRoom: operator.mediaRoomSettings !== undefined,
  MediaFirst: operator.mediaFirstSettings !== undefined,
  BranchID: operator.mediaRoomSettings?.branch ?? null,
  UsesTGOAuth: operator.mediaFirstSettings?.usesTgAuth,
  ChannelMapGroups: [],
  CloudDvrs: operator.mediaFirstSettings?.cloudDvrs
    ? operator.mediaFirstSettings.cloudDvrs.map((cdvr) => ({
        MediaFirstID: cdvr.mediafirstId,
        FeatureSubscriptionID: cdvr.featureSubscriptionId,
        Description: cdvr.description,
        Active: cdvr.active,
      }))
    : undefined,
  DeviceModels: (operator.deviceModels ?? []).map((model) => ({
    Model: model.name,
    CanDVR: model.canDVR,
    ClientStoreValue: model.clientStoreValue ?? null,
    ClientVersion: model.clientVersion ?? null,
    System: model.system ?? 'MR',
    Active: model.active,
  })),
  ExperienceGroups: operator.mediaFirstSettings?.experienceGroups
    ? operator.mediaFirstSettings.experienceGroups.map((eg) => ({
        MediaFirstID: eg.mediafirstId,
        Description: eg.description,
        Active: eg.active,
      }))
    : undefined,
  FIPSCodes: (operator.fipsCodes ?? []).map((code) => ({
    Code: code.code,
    CountyName: code.county,
    Active: true,
  })),
  RoutingGroups: operator.mediaFirstSettings?.routingGroups
    ? operator.mediaFirstSettings.routingGroups.map((rg) => ({
        MediaFirstID: rg.mediafirstId,
        Description: rg.description,
        Active: rg.active,
      }))
    : undefined,
  SubscriberGroups: (operator.subscriberGroups ?? []).map((sg) => ({
    Name: sg.name,
    Description: sg.description,
    Base: sg.type === 'base',
    AlaCarte: sg.type === 'alaCarte',
    Active: sg.active,
  })),
  TransportTypes: (operator.transportTypes ?? []).map((tt) => ({
    Name: tt.name,
    IngressHD: tt.ingressHD ?? 0,
    IngressSD: tt.ingressSD ?? 0,
    WanHD: tt.wanHD ?? 0,
    WanSD: tt.wanSD ?? 0,
    Active: tt.active ?? 0,
  })),
});
